/* Shannon Zackary — live Instagram feed loader
 *
 * Hydrates any element with [data-ig-feed] from a JSON endpoint.
 *
 * Supported endpoint shapes:
 *   - Instagram Graph API:  { data: [{ id, media_type, media_url, thumbnail_url, permalink, caption }] }
 *   - Behold:               { posts: [{ permalink, mediaUrl, sizes: {...}, caption }] }
 *   - rss.app / generic:    [{ image, url, title }]  or  { items: [...] }
 *
 * Cached in sessionStorage for 1 hour to avoid spamming the source.
 * Silently keeps the manual fallback grid if the fetch fails or the endpoint is empty.
 */
(function () {
  const CACHE_MINUTES = 60;
  const containers = document.querySelectorAll('[data-ig-feed]');
  if (!containers.length) return;

  containers.forEach(async (el) => {
    const endpoint = el.dataset.endpoint;
    const count = parseInt(el.dataset.count, 10) || 8;
    if (!endpoint) return;

    // Cache key derived from the endpoint
    const cacheKey = 'sz_ig_' + endpoint.replace(/[^a-zA-Z0-9]/g, '').slice(0, 40);
    const cacheTtl = CACHE_MINUTES * 60 * 1000;

    try {
      // Cache hit?
      const cachedRaw = sessionStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (cached && cached.ts && (Date.now() - cached.ts < cacheTtl) && Array.isArray(cached.posts)) {
          render(el, cached.posts, count);
          return;
        }
      }

      // Fetch fresh
      const res = await fetch(endpoint, { credentials: 'omit' });
      if (!res.ok) throw new Error('Endpoint returned ' + res.status);
      const json = await res.json();
      const posts = normalize(json);

      if (posts.length === 0) throw new Error('No posts in feed');

      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), posts }));
      } catch (_) { /* sessionStorage may be full / disabled — non-fatal */ }

      render(el, posts, count);
    } catch (err) {
      // Silent: leave whatever was server-rendered (the manual fallback)
      if (window.console && console.info) {
        console.info('[Shannon Zackary] Instagram feed not loaded, using fallback. Reason:', err.message);
      }
    }
  });

  function normalize(json) {
    // Instagram Graph API native
    if (json && Array.isArray(json.data)) return json.data.map(normalizeGraphItem);
    // Behold
    if (json && Array.isArray(json.posts)) return json.posts.map(normalizeBeholdItem);
    // rss.app / generic items wrapper
    if (json && Array.isArray(json.items)) return json.items.map(normalizeGenericItem);
    if (json && Array.isArray(json.media)) return json.media.map(normalizeGenericItem);
    // Plain array
    if (Array.isArray(json)) return json.map(normalizeGenericItem);
    return [];
  }

  function normalizeGraphItem(p) {
    const isVideo = p.media_type === 'VIDEO';
    return {
      url: p.permalink || '#',
      image: isVideo ? (p.thumbnail_url || p.media_url) : (p.media_url || p.thumbnail_url),
      caption: (p.caption || '').toString()
    };
  }

  function normalizeBeholdItem(p) {
    const sizes = p.sizes || {};
    return {
      url: p.permalink || '#',
      image: sizes.medium?.mediaUrl || sizes.full?.mediaUrl || p.mediaUrl || p.thumbnailUrl || '',
      caption: (p.caption || '').toString()
    };
  }

  function normalizeGenericItem(p) {
    return {
      url: p.permalink || p.url || p.link || '#',
      image: p.media_url || p.image || p.thumbnail_url || p.thumbnail || p.image_url || p.imageUrl || '',
      caption: (p.caption || p.title || p.description || '').toString()
    };
  }

  function render(el, posts, count) {
    const items = posts
      .filter(p => p.image)
      .slice(0, count)
      .map(p => {
        const altText = (p.caption || '').slice(0, 120).replace(/"/g, '&quot;').replace(/\n/g, ' ');
        return '<a href="' + escapeAttr(p.url) + '" target="_blank" rel="noopener">' +
               '<img loading="lazy" src="' + escapeAttr(p.image) + '" alt="' + altText + '">' +
               '</a>';
      });
    if (items.length === 0) return;
    el.innerHTML = items.join('');
    el.classList.add('ig-bar-grid--live');
  }

  function escapeAttr(s) {
    return String(s || '').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }
})();
